import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Card, CardContent, Chip,
  IconButton, Tooltip, Alert, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableHead, TableRow,
  Tabs, Tab, Switch, FormControlLabel, Accordion, AccordionSummary,
  AccordionDetails, Checkbox, List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import {
  Build as BuildIcon,
  Save as SaveIcon,
  PlayArrow as RunIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../contexts/UserContext';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';

const TranscriptsPage = () => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [hasPermission, setHasPermission] = useState(false);
  const [customReports, setCustomReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [reportForm, setReportForm] = useState({
    name: '',
    description: '',
    dataSource: '',
    filters: [],
    columns: [],
    schedule: 'manual',
    recipients: [],
    format: 'pdf',
    isPublic: false
  });

  useEffect(() => {
    const allowed = !!user;
    setHasPermission(allowed);
    if (allowed) loadCustomReports();
  }, [user]);

  const loadCustomReports = async () => {
    try {
      setLoading(true);
      // Reuse existing endpoints for now
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${ayId || '-'}`;
      const cacheKey = `reports:custom:${ctxKey}`;
      let prefilled = false;
      try { const raw = localStorage.getItem(cacheKey); if (raw) { const v = JSON.parse(raw); if (Array.isArray(v)) { setCustomReports(v); if (v.length) prefilled = true; } } } catch(_) {}
      if (prefilled) setLoading(false);
      let guard; try { guard = setTimeout(() => setLoading(false), 2000); } catch(_) {}
      const data = await fetchJson('/school-admin/reports/custom');
      const arr = data.reports || [];
      setCustomReports(arr);
      try { localStorage.setItem(cacheKey, JSON.stringify(arr)); } catch(_) {}
      setLoading(false); try { clearTimeout(guard); } catch(_) {}
    } catch (err) {
      setError('Failed to load transcripts');
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    try {
      await fetchJson('/school-admin/reports/custom', {
        method: 'POST',
        body: JSON.stringify(reportForm)
      });
      setCreateDialogOpen(false);
      resetForm();
      loadCustomReports();
    } catch (err) {
      setError('Failed to create transcript');
    }
  };

  const handleUpdateReport = async () => {
    try {
      await fetchJson(`/school-admin/reports/custom/${editingReport.id}`, {
        method: 'PUT',
        body: JSON.stringify(reportForm)
      });
      setEditDialogOpen(false);
      setEditingReport(null);
      resetForm();
      loadCustomReports();
    } catch (err) {
      setError('Failed to update transcript');
    }
  };

  const handleDeleteReport = async (id) => {
    if (window.confirm('Are you sure you want to delete this transcript?')) {
      try {
        await fetchJson(`/school-admin/reports/custom/${id}`, {
          method: 'DELETE'
        });
        loadCustomReports();
      } catch (err) {
        setError('Failed to delete transcript');
      }
    }
  };

  const handleRunReport = async (report) => {
    try {
      const response = await fetchJson(`/school-admin/reports/custom/${report.id}/run`, {
        method: 'POST'
      });
      if (response.status === 'completed') {
      }
    } catch (err) {
      setError('Failed to generate transcript');
    }
  };

  const handleEditReport = (report) => {
    setEditingReport(report);
    setReportForm({
      name: report.name,
      description: report.description,
      dataSource: report.dataSource,
      filters: report.filters || [],
      columns: report.columns || [],
      schedule: report.schedule || 'manual',
      recipients: report.recipients || [],
      format: report.format || 'pdf',
      isPublic: report.isPublic || false
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setReportForm({
      name: '',
      description: '',
      dataSource: '',
      filters: [],
      columns: [],
      schedule: 'manual',
      recipients: [],
      format: 'pdf',
      isPublic: false
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getScheduleIcon = (schedule) => {
    switch (schedule) {
      case 'daily':
      case 'weekly':
      case 'monthly':
        return <ScheduleIcon />;
      default:
        return null;
    }
  };

  if (loading && (!customReports || customReports.length === 0)) {
    return (
      <AppLoader variant="circular3" size={60} centered minHeight="60vh" messages={[]} />
    );
  }

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You don't have permission to access this page.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h4" component="h1">
          <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Transcripts
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
          Create Transcript
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="transcripts tabs">
          <Tab label="My Transcripts" icon={<AssessmentIcon />} />
          <Tab label="Public" icon={<ShareIcon />} />
          <Tab label="Scheduled" icon={<ScheduleIcon />} />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          {customReports.filter(report => !report.isPublic).map((report) => (
            <Grid item xs={12} md={6} lg={4} key={report.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h3">{report.name}</Typography>
                    <Box>
                      <IconButton size="small" onClick={() => handleEditReport(report)}><EditIcon /></IconButton>
                      <IconButton size="small" onClick={() => handleDeleteReport(report.id)}><DeleteIcon /></IconButton>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{report.description}</Typography>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip label={report.status} color={getStatusColor(report.status)} size="small" />
                    <Chip label={report.dataSource} size="small" />
                    <Chip label={report.format} size="small" />
                    {report.schedule !== 'manual' && (
                      <Chip label={report.schedule} icon={getScheduleIcon(report.schedule)} size="small" />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" startIcon={<RunIcon />} onClick={() => handleRunReport(report)}>Run</Button>
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon />}>Export</Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          {customReports.filter(report => report.isPublic).map((report) => (
            <Grid item xs={12} md={6} lg={4} key={report.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h3">{report.name}</Typography>
                    <Chip label="Public" color="info" size="small" />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{report.description}</Typography>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip label={report.dataSource} size="small" />
                    <Chip label={report.format} size="small" />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" startIcon={<RunIcon />} onClick={() => handleRunReport(report)}>Run</Button>
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon />}>Export</Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Scheduled Transcripts</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Next Run</TableCell>
                <TableCell>Recipients</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customReports.filter(report => report.schedule !== 'manual').map((report) => (
                <TableRow key={report.id}>
                  <TableCell>{report.name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon />
                      {report.schedule}
                    </Box>
                  </TableCell>
                  <TableCell>{report.nextRun || 'N/A'}</TableCell>
                  <TableCell>
                    {report.recipients?.length > 0 ? (
                      <Chip label={`${report.recipients.length} recipients`} size="small" />
                    ) : (
                      'No recipients'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={report.status} color={getStatusColor(report.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" title="Edit Schedule"><EditIcon /></IconButton>
                    <IconButton size="small" title="Run Now"><RunIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Create Transcript Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Transcript</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name" value={reportForm.name} onChange={(e) => setReportForm({ ...reportForm, name: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={3} value={reportForm.description} onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Data Source</InputLabel>
                <Select value={reportForm.dataSource} onChange={(e) => setReportForm({ ...reportForm, dataSource: e.target.value })} label="Data Source" required>
                  <MenuItem value="students">Students</MenuItem>
                  <MenuItem value="staff">Staff</MenuItem>
                  <MenuItem value="academics">Academics</MenuItem>
                  <MenuItem value="finance">Finance</MenuItem>
                  <MenuItem value="attendance">Attendance</MenuItem>
                  <MenuItem value="custom">Custom Query</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Export Format</InputLabel>
                <Select value={reportForm.format} onChange={(e) => setReportForm({ ...reportForm, format: e.target.value })} label="Export Format">
                  <MenuItem value="pdf">PDF</MenuItem>
                  <MenuItem value="excel">Excel</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Schedule</InputLabel>
                <Select value={reportForm.schedule} onChange={(e) => setReportForm({ ...reportForm, schedule: e.target.value })} label="Schedule">
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={<Switch checked={reportForm.isPublic} onChange={(e) => setReportForm({ ...reportForm, isPublic: e.target.checked })} />} label="Make Public" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateReport} variant="contained" startIcon={<SaveIcon />}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Transcript Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Transcript</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name" value={reportForm.name} onChange={(e) => setReportForm({ ...reportForm, name: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={3} value={reportForm.description} onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Data Source</InputLabel>
                <Select value={reportForm.dataSource} onChange={(e) => setReportForm({ ...reportForm, dataSource: e.target.value })} label="Data Source" required>
                  <MenuItem value="students">Students</MenuItem>
                  <MenuItem value="staff">Staff</MenuItem>
                  <MenuItem value="academics">Academics</MenuItem>
                  <MenuItem value="finance">Finance</MenuItem>
                  <MenuItem value="attendance">Attendance</MenuItem>
                  <MenuItem value="custom">Custom Query</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Export Format</InputLabel>
                <Select value={reportForm.format} onChange={(e) => setReportForm({ ...reportForm, format: e.target.value })} label="Export Format">
                  <MenuItem value="pdf">PDF</MenuItem>
                  <MenuItem value="excel">Excel</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Schedule</InputLabel>
                <Select value={reportForm.schedule} onChange={(e) => setReportForm({ ...reportForm, schedule: e.target.value })} label="Schedule">
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={<Switch checked={reportForm.isPublic} onChange={(e) => setReportForm({ ...reportForm, isPublic: e.target.checked })} />} label="Make Public" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateReport} variant="contained" startIcon={<SaveIcon />}>Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TranscriptsPage;
